import Notion from '../assets/integrations/notion.svg';
import Google from '../assets/integrations/google.svg';
import Dropbox from '../assets/integrations/dropbox.svg';
import env from '../environment';
import { IntegrationState, IntegrationType } from '@sc/graphql';
import z from 'zod';
import { notify } from './notify';


const ZodOAuthStatePayload = z.object({
  type: z.enum(IntegrationType),
  projectUuid: z.string().optional()
}); 

type OAuthStatePayload = z.infer<typeof ZodOAuthStatePayload>; 

const encodeOAuthState = (payload: OAuthStatePayload): string =>
  encodeURIComponent(JSON.stringify(payload));

export const decodeOAuthState = (
  raw: unknown
): OAuthStatePayload | null => {
  if (!raw) return null;
  try {
    const payload = ZodOAuthStatePayload.parse(raw);

    return payload; 
  } catch {
    notify.error("Unable to complete authorisation");
  }

  return null;
};

export const sourceHashForIntegrationType: Record<IntegrationType, string> = {
  [IntegrationType.notion]: 'sources-notion',
  [IntegrationType.googleDrive]: 'sources-google-drive',
  [IntegrationType.dropbox]: 'sources-dropbox',
};

const redirectUri = () =>
  encodeURIComponent(`${window.location.origin}/integrations`);

const notion = {
  name: 'Notion',
  type: IntegrationType.notion,
  logo: Notion,
  description: 'Connect your Notion workspace to sync pages and databases.',
  state: IntegrationState.new,
  oAuthUrl: (projectUuid?: string) =>
    `https://api.notion.com/v1/oauth/authorize?owner=user&client_id=2b2d872b-594c-8086-9a52-0037ebce96ad&redirect_uri=${redirectUri()}&response_type=code&state=${encodeOAuthState({ type: IntegrationType.notion, ...(projectUuid && { projectUuid }) })}`,
};

const googleDrive = {
  name: 'Google Drive',
  type: IntegrationType.googleDrive,
  logo: Google,
  description: 'Access and manage files stored in your Google Drive.',
  state: IntegrationState.new,
  oAuthUrl: (projectUuid?: string) =>
    `https://accounts.google.com/o/oauth2/v2/auth?client_id=${env.googleDriveClientId}&redirect_uri=${redirectUri()}&response_type=code&scope=${encodeURIComponent('https://www.googleapis.com/auth/drive.file')}&access_type=offline&prompt=consent&state=${encodeOAuthState({ type: IntegrationType.googleDrive, ...(projectUuid && { projectUuid }) })}`,
};

const dropbox = {
  name: 'Dropbox',
  type: IntegrationType.dropbox,
  logo: Dropbox,
  description: 'Integrate with Dropbox to sync and share your files.',
  state: IntegrationState.new,
  oAuthUrl: (projectUuid?: string) =>
    `https://www.dropbox.com/oauth2/authorize?client_id=${env.dropboxClientId}&redirect_uri=${redirectUri()}&response_type=code&token_access_type=offline&state=${encodeOAuthState({ type: IntegrationType.dropbox, ...(projectUuid && { projectUuid }) })}`,
};

export const integrationOptions = {
  notion,
  googleDrive,
  dropbox,
};
